import { api } from "@/shared/api/baseApi";
import type {
  AssignRoleBody,
  AssignUserToOrgBody,
  ListUsersQuery,
  ListUsersResponse,
  RemoveRoleBody,
  RemoveUserFromOrgBody,
  UserResponse,
} from "@keystone/contracts/users";

const usersApi = api.enhanceEndpoints({ addTagTypes: ["User"] }).injectEndpoints({
  endpoints: (build) => ({
    listUsers: build.query<ListUsersResponse, ListUsersQuery>({
      query: (args) => ({ url: "/users", params: args }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: "User" as const, id })),
              { type: "User", id: "LIST" },
            ]
          : [{ type: "User", id: "LIST" }],
    }),

    getUser: build.query<UserResponse, string>({
      query: (id) => `/users/${encodeURIComponent(id)}`,
      providesTags: (_r, _e, id) => [{ type: "User", id }],
    }),

    assignRole: build.mutation<UserResponse, { id: string; body: AssignRoleBody }>({
      query: ({ id, body }) => ({
        url: `/users/${encodeURIComponent(id)}/role`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    removeRole: build.mutation<UserResponse, { id: string; body: RemoveRoleBody }>({
      query: ({ id, body }) => ({
        url: `/users/${encodeURIComponent(id)}/role`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    assignToOrg: build.mutation<UserResponse, { id: string; body: AssignUserToOrgBody }>({
      query: ({ id, body }) => ({
        url: `/users/${encodeURIComponent(id)}/organizations`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    removeFromOrg: build.mutation<UserResponse, { id: string; body: RemoveUserFromOrgBody }>({
      query: ({ id, body }) => ({
        url: `/users/${encodeURIComponent(id)}/organizations`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    resetPassword: build.mutation<UserResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/users/${encodeURIComponent(id)}/password-reset`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useListUsersQuery,
  useGetUserQuery,
  useAssignRoleMutation,
  useRemoveRoleMutation,
  useAssignToOrgMutation,
  useRemoveFromOrgMutation,
  useResetPasswordMutation,
} = usersApi;

export { usersApi };
